/**
 * Starfield Component
 * WebGPU-compatible starfield using Points with TSL
 * Slowly rotates around the scene center
 */

import { useRef, useMemo, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { PointsNodeMaterial } from 'three/webgpu'
import { color } from 'three/tsl'

export function Starfield({ 
  count = 5000,
  radius = 200,
  rotationSpeed = 0.0001,
  size = 3
}) {
  const starsRef = useRef()

  // Generate random star positions in a sphere
  const geometry = useMemo(() => {
    const positions = new Float32Array(count * 3)
    
    for (let i = 0; i < count; i++) {
      const i3 = i * 3
      
      // Random position in a sphere
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      const r = radius * (0.5 + Math.random() * 0.5)
      
      positions[i3] = r * Math.sin(phi) * Math.cos(theta)
      positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta)
      positions[i3 + 2] = r * Math.cos(phi)
    }
    
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    return geo
  }, [count, radius])

  // Create WebGPU-compatible points material with TSL
  const material = useMemo(() => {
    const mat = new PointsNodeMaterial()
    mat.colorNode = color(new THREE.Color(1, 1, 1))
    mat.transparent = true
    mat.depthWrite = false
    mat.size = size
    mat.sizeAttenuation = true
    return mat
  }, [size])

  // Cleanup geometry and material on unmount
  useEffect(() => {
    return () => {
      geometry.dispose()
      material.dispose()
    }
  }, [geometry, material])

  // Rotate the starfield slowly - opposite direction to sun
  useFrame((state, delta) => {
    if (starsRef.current) {
      starsRef.current.rotation.y -= rotationSpeed * delta
    }
  })

  return (
    <points ref={starsRef} geometry={geometry} material={material} frustumCulled={false} />
  )
}

export default Starfield
